/**
 * SSR-safe utility to check if E2E test mode is enabled.
 *
 * Checks both build-time (VITE_E2E_TEST_MODE) and runtime (window.__ENV__) configs:
 * - Build-time: For local dev (yarn dev:test, yarn e2e) - no Docker/nginx needed
 * - Runtime: For Kubernetes (Transwarp/prod) - nginx injects window.__ENV__ based on pod env var
 *
 * Why both checks? Local dev uses Vite (build-time), Kubernetes uses Docker (runtime injection).
 * This allows same code to work in both environments without different builds.
 *
 * @returns true if E2E test mode is enabled, false otherwise
 */
export function isE2ETestMode(): boolean {
  // Check build-time config first (SSR-safe, standard Vite pattern)
  if (import.meta.env.VITE_E2E_TEST_MODE === 'true') {
    return true;
  }

  // Check runtime config (client-side only, SSR-safe guard)
  // This is injected by nginx sub_filter when E2E_TEST_MODE=true in container env
  if (
    typeof window !== 'undefined' &&
    window.__ENV__?.E2E_TEST_MODE === 'true'
  ) {
    return true;
  }

  return false;
}

// Declare window.__ENV__ type for runtime config injection
declare global {
  interface Window {
    __ENV__?: {
      E2E_TEST_MODE?: string;
    };
  }
}
